import {
  Box,
  CircularProgress,
  Tooltip,
  Chip,
  Grid,
  Typography,
} from "@mui/material";
import _ from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { API_ROUTES } from "../../../../../../shared/app-configs/sc-app-config";
import { scCacheService } from "../../utils/scCacheService";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
import { InnerButton } from "../../../../../../shared/components/buttons/floating-button-container";
import {
  downloadDocx,
  DownloadDocxOptions,
} from "../../../../../../shared/components/document/docx-downloader";
import FormBuilderV4 from "../../../../../../shared/components/form-builder/form-builder-v4";
import { FormConfigProps } from "../../../../../../shared/components/form-builder/form-builder.model";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import ViewerModalPopup from "../../../../../../shared/components/modals/viewerModal";
import PdfViewer from "../../../../../../shared/components/preview/pdf-viewer";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { useApiV4 } from "../../../../../../shared/hooks/useApiV4";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { useScStore } from "../../../../../../store/scStandard.store";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import { RestrictedDataValidation } from "../../../../../../shared/utils/custom-validation.utils";
import { Cancel } from "@mui/icons-material";

// Data structure for claims categories
interface ClaimsCategoryData {
  claimsCategories: string[];
}
// Data structure for claims categories
interface SCProductComponetData {
  scProductComponentCode: string[];
}

// Define the interface for API Response
export interface ComponentDetailsResponse {
  scId: string;
  componentId: string;
  scProductComponentCode: string;
  scComponentDetail: string;
  scProductDetailCode: string;
}

export interface ComponentDetailsApiResponse {
  scId: string;
  scProductComponentCode: string[];
}

export interface ProductClaimsResponse {
  scId: string;
  scProductClaimsCategory: string[];
}

interface ComponentClaimsCategoryDetailsTabProps {
  scStatus?: boolean;
}

const ComponentClaimsCategoryDetailsTab: React.FC<
  ComponentClaimsCategoryDetailsTabProps
> = ({ scStatus }) => {
  const navigate = useNavigate();
  const { SC_ID, formtype } = useParams();
  const formRef = useRef<any>(null);
  const componentInfoFormRef = useRef<any>(null);
  // useApiV4: V4-only hook that returns the 422 payload (needed for cross-tab validation on submit)
  const { executePost, loading: isSaving } = useApiV4();
  // useApi: shared hook for GETs and DELETEs — no 422 return needed here
  const { executeGet, executeDelete, loading: isLoading } = useApi();

  // State for component information table
  const [componentData, setComponentData] = useState<string[]>([]);
  const [claimsData, setClaimsData] = useState<string[]>([]);
  const [componentCodeOptions, setComponentCodeOptions] = useState<any[]>([]);
  const [scClaimsCodeOptions, setScClaimsCodeOptions] = useState<any[]>([]);
  // Read SC standards + PR0035 flag from the shared Zustand store (populated by the SC Details tab).
  // hasPR0035 is used as a fallback in submit pre-validation when fresh facility fetch returns empty.
  const { scStandards, hasPR0035 } = useScStore();
  const { setTabError, setSavedClaims } = useScStore();

  // State for claims categories form
  const [claimsCategoryData, setClaimsCategoryData] = useState<string[]>([]);

  // True while ALL initial data fetches are in-flight — hides stale content until ready
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Button states
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { isRestrictPopUpSave, setRestrictPopUpSave, cbLicensingCode } =
    useTransactionCertificateStore();
  const { notify } = useNotification();

  const hasSubmitSC: boolean = formtype === "submited-sc";
  const isReadOnlyBasedOnStatus = hasSubmitSC || scStatus;

  // True when the user has changed claims/components since the last successful save.
  // The Submit button is disabled while this is true — user must Save first.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Track whether the initial data load is complete so we don't false-trigger
  // hasUnsavedChanges when the API pre-populates the form on mount.
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Fetch standards only when the Zustand store is empty (edge case: user lands
  // directly on this tab without visiting SC Details tab first).
  const fetchStandardsFromSCDetails = async () => {
    if (!SC_ID) return;
    // Store already populated by SC Details tab — skip the network call
    if (useScStore.getState().scStandards.length > 0) return;
    try {
      const result = await executeGet(API_ROUTES.GET_SC_DETAILS_V4, { scId: SC_ID });
      const standards = result?.data?.scStandardInformation?.map(
        (item: any) => item.scStandard
      ) || [];
      useScStore.getState().setScStandards(standards);
    } catch (error) {
      console.error("Error fetching SC standards:", error);
      notify("Error fetching standards", "error");
    }
  };

  const loadInitialValues = async() => {
    setIsInitialLoading(true);
    setIsDataLoaded(false);
    try {
      await getComponentDetails();
      await getProductClaims();
      await getComponentCodes();
      await getSCClaimsCodes();
      await fetchStandardsFromSCDetails();
    } finally {
      setIsInitialLoading(false);
      // Mark data as loaded AFTER all fetches complete so that the downstream
      // useEffects that react to claimsCategoryData don't flag a false unsaved change.
      setTimeout(() => setIsDataLoaded(true), 0);
    }
  }

  // Fetch data on page load
  useEffect(() => {
    loadInitialValues();
  }, [SC_ID]);

  // Fetch existing component details from API
  const getComponentDetails = async () => {
    if (!SC_ID) return;

    try {
      const response = await executeGet(API_ROUTES.GET_COMPONENT_DETAILS_V4, {
        scId: SC_ID,
      });

      if (response?.status === 200 && response.data) {
        const apiData: any = response.data;

        if (apiData.scProductComponentCode) {
          // Transform API response to component data format
          const transformedData: string[] = apiData?.scProductComponentCode;
          setComponentData(
            transformedData.map((item) => item.split(";")[0].trim())
          );
        }
      }
    } catch (error) {
      console.error("Error fetching component details:", error);
      notify("Error fetching component details", "error");
    }
  };

  // Fetch existing product claims from API
  const getProductClaims = async () => {
    if (!SC_ID) return;

    try {
      const response = await executeGet(
        API_ROUTES.GET_PRODUCT_CLAIMS_DETAILS_V4,
        { scId: SC_ID }
      );

      if (response?.status === 200 && response.data) {
        const apiData: ProductClaimsResponse = response.data;

        // Only pre-populate if the API returned a non-empty array.
        // An empty or missing value means no claims were explicitly saved
        // and the dropdown should start empty (user must select).
        if (Array.isArray(apiData.scProductClaimsCategory) && apiData.scProductClaimsCategory.length > 0) {
          setClaimsCategoryData(apiData.scProductClaimsCategory);
          // Mirror to Zustand so the parent stepper can proactively detect
          // missing-claims errors without the user needing to visit this tab first.
          setSavedClaims(apiData.scProductClaimsCategory);
        }
      }
    } catch (error) {
      console.error("Error fetching product claims:", error);
      notify("Error fetching product claims", "error");
    }
  };

  // Fetch component codes from API
  const getComponentCodes = async () => {
    try {
      const queryParams = {
        query_param: TCAppConfig.GET_PRODUCT_DETAIL_CODE,
      };
      const response = await executeGet(
        TCAppConfig.GET_DTRACKIT_REF_CODE_V4,
        queryParams
      );
      if (response?.status === 200 && response.data) {
        const options = response.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1 ?? ""} (${t.code_1})`,
          code: `${t.code_1}; ${t.description_1}`,
        }));

        // Add NA option
        const naOption = {
          value: "na",
          label: "na",
          code: "na",
        };

        setComponentCodeOptions([...options, naOption]);
      }
    } catch (err) {
      console.error("Error in Fetching Component Codes:", err);
      notify("Error fetching component codes", "error");
    }
  };
  // Fetch SC Claims codes from API using v4 service
  const getSCClaimsCodes = async () => {
    try {
      const response =
        await dtrackItServiceV4.getCertifiedOrganizationApprovedClaimsValuesLG();
      if (response?.data) {
        const options = response.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1}`,
          code: t.code_1,
        }));
        setScClaimsCodeOptions(options);
      }
    } catch (err) {
      console.error("Error in Fetching SC Claims Codes:", err);
      notify("Error fetching SC claims codes", "error");
    }
  };

  // Validate that every SC standard has at least one selected Product Claims Category.
  // Claim codes follow the convention "{STANDARD_CODE}{suffix}" (e.g. RMS001, GRS-CL01),
  // so we check that the claim code starts with the standard code.
  const validateAllStandardsHaveClaim = (): { valid: boolean; missing: string[] } => {
    if (!scStandards.length) return { valid: true, missing: [] };
    const missing = scStandards.filter((standard) =>
      !claimsCategoryData.some((claim) => claim.startsWith(standard))
    );
    return { valid: missing.length === 0, missing };
  };

  // Button handlers following facility-product-details pattern
  const handleSCSave = async () => {
    try {
      if (!componentData || _.isEmpty(componentData)) {
        notify("Please add component information details", "error");
        return;
      }
      if (!claimsCategoryData || _.isEmpty(claimsCategoryData)) {
        notify("Please add SC Claims information details", "error");
        return;
      }

      // Validate: at least one claim must be selected for EACH standard
      const missingStandards = scStandards.filter(
        (standard) => !claimsCategoryData.some((claim) => claim.startsWith(standard))
      );
      if (missingStandards.length > 0) {
        notify(
          `Please select at least one claims category for standard(s): ${missingStandards.join(", ")}`,
          "error"
        );
        return;
      }

      // Submit the form to save claims categories first
      if (componentInfoFormRef.current) {
        await componentInfoFormRef.current.submitForm();
      }
      // Submit the form to save claims categories first
      if (formRef.current) {
        await formRef.current.submitForm();
      }
      //componentInfoFormConfig
      // Save scope certificate data
      const params = {
        scId: SC_ID,
        scCblicensingCode: cbLicensingCode,
      };
      const result: any = await executePost(API_ROUTES.SAVE_SC_V4, params);
      if (result.status === 200) {
        notify("Saved Successfully", "success");
        // Bust the facility list cache so the next submit pre-check always reads fresh data.
        useScStore.getState().clearFacilityListCache();
        // Surgical update: write the just-saved claims to Zustand (L1) and localStorage (L2).
        // Next page load will serve these from localStorage — 0 API calls needed.
        setSavedClaims(claimsCategoryData);
        if (SC_ID) scCacheService.setClaims(SC_ID, claimsCategoryData);
        setHasUnsavedChanges(false); // Clear dirty flag — Submit is now enabled
        setIsSubmitted(false);
      } else {
        notify("Unable to Save", "error");
      }
    } catch (error) {
      console.error("Error saving Component & Claims Category Details:", error);
      notify("Error saving Component & Claims Category Details", "error");
    }
  };

  const handleSCSubmit = async () => {
    try {
      if (!componentData || _.isEmpty(componentData)) {
        notify("Please add component information details", "error");
        return;
      }
      if (!claimsCategoryData || _.isEmpty(claimsCategoryData)) {
        notify("Please select at least one SC Product Claims Category", "error");
        return;
      }

      // Validate: at least one claim must be selected for EACH standard
      const missingClaimStandards = scStandards.filter(
        (standard) => !claimsCategoryData.some((claim) => claim.startsWith(standard))
      );
      if (missingClaimStandards.length > 0) {
        notify(
          `Please select at least one claims category for standard(s): ${missingClaimStandards.join(", ")}`,
          "error"
        );
        return;
      }

      // Pre-submit: validate facility standards AND detect PR0035 in one API call.
      // Always fetch fresh so we validate against the latest saved data.
      let hasPR0035FromFacilities = false;
      if (SC_ID) {
        try {
          useScStore.getState().clearFacilityListCache();
          const facilityListResp = await executeGet(API_ROUTES.GET_FACILITY_LIST_DETAILS_V4, { scId: SC_ID });

          // Use the same parsing as the original mismatch check — proven to work with
          // the actual API response format. The list endpoint returns:
          //   { data: { response: { facilityDetails: [...] } } }
          const facilities: any[] = facilityListResp?.data?.response?.facilityDetails || [];
          useScStore.getState().setFacilityListCache(facilityListResp?.data?.response);

          // ── Standard mismatch check ───────────────────────────────────────────
          if (scStandards.length > 0) {
            const standardMismatches: string[] = [];
            for (const facility of facilities) {
              const facilityName = facility.facilityName || "Unknown Facility";
              const facilityTeId = facility.facilityTeId || facility.facilityId || "";
              // facilityStandard may come as plain strings ["RWS"] or objects [{scStandard:"RWS"}].
              // Normalize to plain strings to make the includes() comparison reliable.
              const rawStds = Array.isArray(facility.facilityStandard)
                ? facility.facilityStandard
                : [facility.facilityStandard].filter(Boolean);
              const facilityStds: string[] = rawStds.map((s: any) =>
                typeof s === 'string' ? s : (s?.scStandard || '')
              ).filter(Boolean);

              const missing = scStandards.filter((s: string) => !facilityStds.includes(s));
              if (missing.length > 0) {
                standardMismatches.push(
                  `'${facilityName}' (TE-ID: ${facilityTeId}) is missing standard(s): ${missing.join(", ")}`
                );
              }
            }
            if (standardMismatches.length > 0) {
              notify(
                `Facility standard mismatch — please go to Facility Information and update: ${standardMismatches.join(" | ")}`,
                "error"
              );
              return;
            }
          }

          // ── PR0035 detection from facility list ───────────────────────────────
          // The facilityProcessDetails field is returned by the list endpoint.
          // Also check facilityProcessInformation as a fallback (single-facility endpoint name).
          hasPR0035FromFacilities = facilities.some((f: any) => {
            const processList = f?.facilityProcessDetails || f?.facilityProcessInformation || [];
            return Array.isArray(processList) && processList.some(
              (p: any) => p?.facilityProcessCategoryCode === 'PR0035'
            );
          });
        } catch (facilityCheckErr) {
          console.warn("Pre-submit facility check failed (non-blocking):", facilityCheckErr);
        }
      }

      // ── PR0035 cross-tab pre-validation ───────────────────────────────────────
      // Use the freshly-computed hasPR0035FromFacilities (from the fetch above).
      // Fall back to the Zustand hasPR0035 only if the facility fetch returned nothing.
      const pr0035Active = hasPR0035FromFacilities || hasPR0035;
      if (pr0035Active) {
        try {
          const scDetailsResp = await executeGet(API_ROUTES.GET_SC_DETAILS_V4, { scId: SC_ID });
          const rawSCData = scDetailsResp?.data;

          const brandNames: string[] = rawSCData?.brandName || [];
          const approvedClaims: string = rawSCData?.certifiedOrganizationApprovedClaims || '';

          // Collect ALL errors first, then navigate once — so the user sees both
          // errors highlighted at once instead of fixing one at a time.
          const pr0035Errors: string[] = [];
          if (!brandNames || brandNames.length === 0) {
            pr0035Errors.push('brandName: Brand Name is required when process code PR0035 is present');
          }
          if (!approvedClaims || approvedClaims.trim() === '' || approvedClaims.trim().toUpperCase() === 'NONE') {
            pr0035Errors.push('certifiedOrganizationApprovedClaims: Certified Organization Approved Claims is required and cannot be "NONE" when process code PR0035 is present');
          }

          if (pr0035Errors.length > 0) {
            useScStore.getState().clearBackendValidationErrors();
            useScStore.getState().setBackendValidationErrors('scope-certificate-details', pr0035Errors);
            // Show one notification per missing field
            if (brandNames.length === 0) {
              notify('Brand Name is required when process code PR0035 is present.', 'error');
            }
            if (!approvedClaims || approvedClaims.trim() === '' || approvedClaims.trim().toUpperCase() === 'NONE') {
              notify('Certified Organization Approved Claims is required and cannot be "NONE" when process code PR0035 is present.', 'error');
            }
            navigate(`/v4/sc/${formtype}/scope-certificate-details/${SC_ID}`);
            return;
          }
        } catch (pr0035Err) {
          console.warn('[PR0035 pre-validation] SC details fetch failed — letting backend validate:', pr0035Err);
        }
      }

      const params = {
        scId: SC_ID,
      };
      // Clear previous validation errors
      useScStore.getState().clearBackendValidationErrors();

      const result: any = await executePost(API_ROUTES.SUBMIT_SC_V4, params);

      if (!result) return;
      
      if (result.status === 200) {
        notify("Submitted Successfully", "success");
        // ── Post-submit cache purge ────────────────────────────────────────────
        // Wipe all L1 Zustand transient state AND the L2 localStorage cache for
        // this SC so the next session (new SC or editing a different SC) always
        // starts completely clean. This is the definitive end of this SC session.
        useScStore.getState().clearAllTransientState();
        useScStore.getState().setPersistedScId(null);
        if (SC_ID) scCacheService.clearAll(SC_ID);
        setIsSubmitted(false);
      } else if (result.status === 422 && result.data) {
        // Backend Validation Error (422) handling
        const rawDetail = result.data.detail;
        const mappedErrors: Record<string, string[]> = {};

        // ── Handle plain-string detail (e.g. PR0035 custom backend errors) ──────
        // Pydantic errors return an array; custom errors return a plain string.
        if (typeof rawDetail === 'string') {
          const msgLower = rawDetail.toLowerCase();
          let targetTab = 'component-details'; // default: stay here
          if (
            msgLower.includes('brandname') || msgLower.includes('brand name') ||
            msgLower.includes('certifiedorganization') || msgLower.includes('approved claims') ||
            msgLower.includes('pr0035')
          ) {
            targetTab = 'scope-certificate-details';
          }
          useScStore.getState().setBackendValidationErrors(targetTab, [rawDetail]);
          notify(rawDetail, 'error');
          if (targetTab !== 'component-details') {
            navigate(`/v4/sc/${formtype}/${targetTab}/${SC_ID}`);
          }
          return;
        }

        // ── Handle array-of-objects detail (standard Pydantic 422 errors) ───────
        const errors = Array.isArray(rawDetail) ? rawDetail : [];

        // Map backend API field names → tab IDs.
        // Keys are ALL LOWERCASE so the lookup below is case-insensitive.
        // The backend can return fields in any casing (e.g. 'BrandName', 'brandName', 'brand_name').
        const FIELD_TAB_MAP: Record<string, string> = {
          scstandardinformation: "scope-certificate-details",
          scopecertificateno: "scope-certificate-details",
          brandname: "scope-certificate-details",
          certifiedorganizationapprovedclaims: "scope-certificate-details",
          scnumberoffarms: "scope-certificate-details",
          isfarmsc: "scope-certificate-details",
          scopecertificatedates: "scope-certificate-details",
          facilitydetails: "facility-details",
          certifiedorganizationdetails: "certified-organization-details",
          scproductcategoryinformation: "product-category-information",
          scproductdetailinformation: "product-detail-information",
          scrawmaterialdetails: "raw-material-details",
          scproductcomponentcode: "component-details",
          scproductclaimscategory: "component-details",
        };

        let firstErrorTab: string | null = null;

        errors.forEach((errObj: any) => {
          // Normalize field name — try loc first, then fall back to parsing the message
          let rawField = errObj.loc ? errObj.loc[errObj.loc.length - 1] : "";
          const fieldNormalized = String(rawField).toLowerCase().replace(/_/g, '');
          const message = errObj.msg || "Validation error";

          // If loc is missing, try to infer the tab from the message content
          let targetTab = FIELD_TAB_MAP[fieldNormalized] || null;
          if (!targetTab) {
            const msgLower = message.toLowerCase();
            if (msgLower.includes('brandname') || msgLower.includes('brand name') ||
                msgLower.includes('certifiedorganization') || msgLower.includes('pr0035')) {
              targetTab = 'scope-certificate-details';
              rawField = rawField || 'brandName';
            } else {
              targetTab = "component-details";
            }
          }

          if (!mappedErrors[targetTab]) {
            mappedErrors[targetTab] = [];
          }
          mappedErrors[targetTab].push(`${rawField}: ${message}`);

          if (!firstErrorTab) {
            firstErrorTab = targetTab;
          }
        });

        // Save mapped errors to Zustand store
        Object.entries(mappedErrors).forEach(([tabId, messages]) => {
          useScStore.getState().setBackendValidationErrors(tabId, messages);
        });

        notify("Validation failed. Please correct the highlighted errors.", "error");

        // Navigate to the first tab that has an error
        if (firstErrorTab) {
          navigate(`/v4/sc/${formtype}/${firstErrorTab}/${SC_ID}`);
        }
      } else {
        const errMsg = result.message || "Unable to Submit";
        notify(errMsg, "error");
      }
    } catch (error) {
      console.error(
        "Error submitting Component & Claims Category Details:",
        error
      );
      notify("Error submitting Component & Claims Category Details", "error");
    }
  };


  const togglePreviewDrawer = () => setPreviewOpen((prev) => !prev);

  // Filter claim codes based on selected standards
  const filteredScClaimsCodeOptions = useMemo(() => {
    if (!scStandards.length || !scClaimsCodeOptions.length) {
      return scClaimsCodeOptions;
    }

    return scClaimsCodeOptions.filter((option: any) => {
      // Check if the claim code starts with any of the standard codes
      return scStandards.some((standard: string) => 
        option.code.startsWith(standard)
      );
    });
  }, [scStandards, scClaimsCodeOptions]);

  // When the available claim options change (because standards changed),
  // silently clear any selected claim that is no longer valid.
  // The missing-claims banner below will guide the user on what to re-select.
  useEffect(() => {
    if (filteredScClaimsCodeOptions.length === 0) return; // options not loaded yet
    const validCodes = new Set(filteredScClaimsCodeOptions.map((o: any) => o.value));
    setClaimsCategoryData(prev => prev.filter(c => validCodes.has(c)));
  }, [filteredScClaimsCodeOptions]);

  // Track unsaved changes: any time claims or components change AFTER initial load,
  // mark as unsaved so the Submit button is disabled until the user saves.
  useEffect(() => {
    if (!isDataLoaded) return;
    setHasUnsavedChanges(true);
  }, [claimsCategoryData, componentData]);

  // Signal to the parent stepper whether this tab has active errors.
  // NO cleanup — error persists in Zustand until user actually fixes missing claims.
  // Cleared in full when parent stepper unmounts (SC session ends / SC_ID changes).
  useEffect(() => {
    const missingClaimsStandards = scStandards.filter(
      (std: string) => !claimsCategoryData.some((claim: string) => claim.startsWith(std))
    );
    setTabError('component-details', missingClaimsStandards.length > 0);
  }, [claimsCategoryData, scStandards]);

  const handleDownload = async () => {
    setIsDownloading(true);
    const options: DownloadDocxOptions = {
      apiUrl: API_ROUTES.GET_WORD_DOC_V4,
      params: {
        scId: SC_ID ?? "",
        cbCode: cbLicensingCode,
      },
      headers: {
        // Optional headers if needed
      },
    };

    try {
      notify("Starting Download", "info");
      await downloadDocx(options);
      notify("File Downloaded", "success");
      setIsDownloading(false);
    } catch (error) {
      notify("Failed to download DOCX", "error");
      setIsDownloading(false);
    }
  };

  // Form configuration for claims categories
  const formConfig: FormConfigProps = {
    initialValues: claimsCategoryData,
    validationSchema: Yup.object().shape({
      claimsCategories: Yup.array().min(
        1,
        "At least one claims category is required"
      ),
    }),
    readOnly: isReadOnlyBasedOnStatus,
    sections: [
      {
        // title: " SC Product Claims Category",
        fields: [
          {
            name: "claimsCategories",
            label: "SC Product Claims Category",
            type: "select",
            required: true,
            gridSize: { xs: 12, md: 12 },
            events: {
              onChange: async (
                updatedValue: any,
                formValues: any,
                setFieldValue: (arg0: string, arg1: any) => void
              ) => {
                setClaimsCategoryData(updatedValue);
              },
            },
            props: {
              placeholder: "Select SC Product Claims Category",
              options: filteredScClaimsCodeOptions,
              multiple: true,
              enableSearch: true,
              value: claimsCategoryData,
              width: "100%",
              showCodeOnlyWhenSelected: true,
            },
          },
        ],
      },
    ],
    pageName: "",
    handleSubmit: async (values: ClaimsCategoryData) => {
      try {
        if (!claimsCategoryData || _.isEmpty(claimsCategoryData)) {
          notify("Please add claims details", "error");
          return false;
        }

        // Save the claims categories first
        const claimsParams = {
          scId: SC_ID,
          scProductClaimsCategory: claimsCategoryData,
        };

        const claimsResult: any = await executePost(
          API_ROUTES.POST_PRODUCT_CLAIMS_DETAILS_V4,
          claimsParams
        );

        if (claimsResult.status === 200 || claimsResult.status === 201) {
          notify("Claims categories saved successfully", "success");
          return true;
        } else {
          notify("Failed to save claims categories", "error");
          return false;
        }
      } catch (error) {
        console.error("Error saving claims categories:", error);
        notify("Error saving claims categories", "error");
        return false;
      }
    },
  };
  console.log(isReadOnlyBasedOnStatus, "isReadOnlyBasedOnStatus");

  // Form configuration for claims categories
  const componentInfoFormConfig: FormConfigProps = {
    initialValues: componentData,
    validationSchema: Yup.object().shape({
      scProductComponentCode: Yup.array()
        .of(
          Yup.string().test(
            "no-restricted-data",
            "NA, N/A, Not available, n/a, NA are not allowed",
            (value) => !RestrictedDataValidation.isRestrictedData(value)
          )
        )
        .min(1, "At least one component code is required"),
    }),
    readOnly: isReadOnlyBasedOnStatus,
    sections: [
      {
        // title: " Component Information",
        fields: [
          {
            name: "scProductComponentCode",
            label: "SC Product Component Code ",
            type: "select",
            required: true,
            gridSize: { xs: 12, md: 12 },
            helperText:
              'Please select "na" if Product Component Code is not applicable',
            events: {
              onChange: async (
                updatedValue: any,
                formValues: any,
                setFieldValue: (arg0: string, arg1: any) => void
              ) => {
                // Enforce mutual exclusivity: if 'na' is selected, it must be the only selection
                const values = Array.isArray(updatedValue) ? updatedValue : [];
                const hasNa = values.includes('na');
                let nextValues = values;

                if (hasNa && values.length > 1) {
                  nextValues = ['na'];
                } else if (!hasNa) {
                  nextValues = values.filter((v) => v !== 'na');
                }

                setComponentData(nextValues);
                setFieldValue('scProductComponentCode', nextValues);
              },
            },
            props: {
              enableSearch: true,
              placeholder: "Select SC Product Component Code",
              options: componentCodeOptions,
              value: componentData,
              multiple: true,
              isOptionDisabled: (option: any, currentValue: any) => {
                const selected = Array.isArray(currentValue) ? currentValue : [];
                const hasNaSelected = selected.includes('na');
                // If 'na' is selected, disable all others
                if (hasNaSelected) return option.value !== 'na';
                // If any non-'na' selected, disable 'na'
                if (!hasNaSelected && selected.length > 0 && option.value === 'na') return true;
                return false;
              },
              width: "100%",
              showCodeOnlyWhenSelected: true,
            },
          },
        ],
      },
    ],
    pageName: "",
    handleSubmit: async (values: any) => {
      try {
        if (!componentData || _.isEmpty(componentData)) {
          notify("Please add component information details", "error");
          return false;
        }

        // Save the claims categories first
        const queryParams = {
          scId: SC_ID,
          scProductComponentCode: componentData.map((val: string) => {
            return componentCodeOptions?.find(
              (compCode) => compCode?.value === val
            )?.code;
          }),
        };

        const componentResult: any = await executePost(
          API_ROUTES.POST_COMPONENT_DETAILS_V4,
          queryParams
        );

        if (componentResult.status === 200 || componentResult.status === 201) {
          notify("SC Product Components saved successfully", "success");
          return true;
        } else {
          notify("Failed to save SC Product Components", "error");
          return false;
        }
      } catch (error) {
        console.error("Error saving SC Product Components:", error);
        notify("Error saving SC Product Components", "error");
        return false;
      }
    },
  };
  const handleDeleteComponent = (itemToDelete: any) => {
    setComponentData((prev) => prev.filter((item) => item !== itemToDelete));
  };
  const handleDeleteClaims = (itemToDelete: any) => {
    setClaimsCategoryData((prev) =>
      prev.filter((item) => item !== itemToDelete)
    );
  };

  return (
    <>
      {isInitialLoading ? (
        <LoaderComponent />
      ) : (
        <>
          {/* Compact missing-claims bar + unsaved-changes notice */}
          {(() => {
            const missingClaimsStandards = scStandards.filter(
              std => !claimsCategoryData.some(claim => claim.startsWith(std))
            );
            if (missingClaimsStandards.length === 0 && !hasUnsavedChanges) return null;
            return (
              <Box mb={1} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1,
                bgcolor: missingClaimsStandards.length > 0 ? "#fff0f0" : "#fff8e1",
                border: `1px solid ${missingClaimsStandards.length > 0 ? "#e53935" : "#f9a825"}`,
                borderRadius: "4px", px: 1.5, py: 0.75 }}>

                {missingClaimsStandards.length > 0 && (
                  <>
                    <Typography variant="caption" sx={{ color: "#b71c1c", fontWeight: 700, mr: 0.5 }}>
                      ⛔ Missing claims category for:
                    </Typography>
                    {missingClaimsStandards.map((std, i) => (
                      <Tooltip key={i} arrow title={`No claims category selected for standard "${std}" — select one from the SC Product Claims Category dropdown`}>
                        <Chip
                          label={std}
                          size="small"
                          sx={{ bgcolor: "#ffcdd2", color: "#b71c1c", fontWeight: 700,
                            fontSize: "0.7rem", height: 20, cursor: "help" }}
                        />
                      </Tooltip>
                    ))}
                    <Typography variant="caption" sx={{ color: "#7f0000" }}>
                      — select a claims category for each highlighted standard
                    </Typography>
                  </>
                )}

                {hasUnsavedChanges && (
                  <Typography variant="caption" sx={{
                    color: "#7a4100", fontWeight: 600,
                    ml: missingClaimsStandards.length > 0 ? 1 : 0,
                    borderLeft: missingClaimsStandards.length > 0 ? "1px solid #f9a825" : "none",
                    pl: missingClaimsStandards.length > 0 ? 1 : 0,
                  }}>
                    ⚠ Unsaved changes — click <strong>Save</strong> before submitting
                  </Typography>
                )}
              </Box>
            );
          })()}

          {/* Side-by-Side Responsive Grid Container */}
          <Grid container spacing={3} sx={{ width: "100%", mb: 2, boxSizing: "border-box" }}>
            {/* Left Side: Component Information Section */}
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 4, width: "100%" }}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  align="left"
                  fontWeight={"600"}
                >
                  Component Information
                </Typography>

                <Box sx={{ border: "2px solid rgba(128, 128, 128, .2)", width: "100%", padding: "2rem", borderRadius: 2, boxSizing: "border-box" }}>
                  <FormBuilderV4
                    formConfig={componentInfoFormConfig}
                    onSubmit={componentInfoFormConfig?.handleSubmit}
                    formRef={componentInfoFormRef}
                  />

                  <Box
                    sx={{
                      maxHeight: 3 * 56,
                      overflowY: "auto",
                      width: "100%",
                      padding: 1,
                      borderRadius: 2,
                      boxSizing: "border-box",
                    }}
                  >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, width: "100%" }}>
                      {componentData.map((item: any, index: number) => {
                        const option = componentCodeOptions?.find((opt: any) => opt.value === item);
                        const fullLabel = option ? option.label : item;
                        return (
                          <Tooltip key={index} title={fullLabel} arrow>
                            <Chip
                              label={item}
                              onDelete={isReadOnlyBasedOnStatus ? undefined : () => handleDeleteComponent(item)}
                              deleteIcon={isReadOnlyBasedOnStatus ? <></> : <Cancel />}
                              variant="outlined"
                              sx={{
                                borderRadius: "16px",
                                maxWidth: "180px",
                                "& .MuiChip-label": {
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                },
                              }}
                            />
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Right Side: SC Product Claims Category Section */}
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 4, width: "100%" }}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  align="left"
                  fontWeight={"600"}
                >
                  SC Product Claims Category
                </Typography>
                <Box sx={{ border: "2px solid rgba(128, 128, 128, .2)", width: "100%", padding: "2rem", borderRadius: 2, boxSizing: "border-box" }}>
                  <FormBuilderV4
                    formConfig={formConfig}
                    onSubmit={formConfig.handleSubmit}
                    formRef={formRef}
                  />

                  <Box
                    sx={{
                      maxHeight: 3 * 56,
                      overflowY: "auto",
                      width: "100%",
                      padding: 1,
                      borderRadius: 2,
                      boxSizing: "border-box",
                    }}
                  >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, width: "100%" }}>
                      {claimsCategoryData.map((item: any, index: number) => {
                        const option = filteredScClaimsCodeOptions?.find((opt: any) => opt.value === item)
                          || scClaimsCodeOptions?.find((opt: any) => opt.value === item);
                        const fullLabel = option ? option.label : item;
                        return (
                          <Tooltip key={index} title={fullLabel} arrow>
                            <Chip
                              label={item}
                              onDelete={isReadOnlyBasedOnStatus ? undefined : () => handleDeleteClaims(item)}
                              deleteIcon={isReadOnlyBasedOnStatus ? <></> : <Cancel />}
                              variant="outlined"
                              sx={{
                                borderRadius: "16px",
                                maxWidth: "180px",
                                "& .MuiChip-label": {
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                },
                              }}
                            />
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
              gap: "0.5rem",
              width: "100%",
            }}
          >
            <NavLink to="/v4/sc-dashboard">
              <InnerButton>Home</InnerButton>
            </NavLink>

            <InnerButton
              onClick={handleSCSave}
              // disabled={isReadOnlyBasedOnStatus}
            >
              Save
            </InnerButton>
            {(componentData?.length > 0 || claimsCategoryData?.length > 0) && (
              <>
              {(() => {
                  const missingClaimsStandards = scStandards.filter(
                    std => !claimsCategoryData.some(claim => claim.startsWith(std))
                  );
                  const isSubmitDisabled = hasUnsavedChanges || missingClaimsStandards.length > 0;
                  const submitTooltip = hasUnsavedChanges
                    ? "Please save your changes before submitting"
                    : missingClaimsStandards.length > 0
                    ? `Please select a claims category for standard(s): ${missingClaimsStandards.join(", ")}`
                    : "";
                  return (
                    <Tooltip title={submitTooltip} disableHoverListener={!isSubmitDisabled}>
                      <span>
                        <InnerButton
                          onClick={handleSCSubmit}
                          disabled={isSubmitDisabled}
                          sx={{ opacity: isSubmitDisabled ? 0.5 : 1 }}
                        >
                          Submit
                        </InnerButton>
                      </span>
                    </Tooltip>
                  );
                })()}

                <Tooltip title={"Click to Preview"}>
                  <span>
                    <InnerButton onClick={togglePreviewDrawer}>
                      Preview
                    </InnerButton>
                  </span>
                </Tooltip>

                <Tooltip title={"Click to Download"}>
                  <span>
                    <InnerButton
                      onClick={handleDownload}
                      disabled={isDownloading}
                      sx={{
                        position: "relative",
                        "& .loading-indicator": {
                          position: "absolute",
                          left: "50%",
                          transform: "translateX(-50%)",
                        },
                      }}
                    >
                      {isDownloading ? (
                        <Box
                          className="loading-indicator"
                          display="flex"
                          alignItems="center"
                        >
                          <CircularProgress size={20} color="inherit" />
                        </Box>
                      ) : (
                        "Download Word"
                      )}
                    </InnerButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>

          {/* Preview Modal */}
          {SC_ID && (
            <ViewerModalPopup
              open={previewOpen}
              onClose={togglePreviewDrawer}
              title="Preview"
            >
              <div style={{ width: "100%", height: "100vh" }}>
                <PdfViewer
                  apiUrl={API_ROUTES.GET_PREVIEW_SC_V4}
                  params={{ scId: SC_ID }}
                />
              </div>
            </ViewerModalPopup>
          )}
        </>
      )}
    </>
  );
};

export default ComponentClaimsCategoryDetailsTab;
